import { useEffect, useState, useCallback, useRef } from "react";
import api, { formatApiError, downloadFile } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "@/components/ui/table";
import { toast } from "sonner";
import {
  Loader2, Database, FileSpreadsheet, Eye, CheckCircle2, XCircle,
  Copy, ListChecks, History, Upload, FileDown,
} from "lucide-react";
import { paxResumen } from "@/lib/constants";

function FilaBadge({ fila }) {
  if (fila.duplicada)
    return <span className="inline-flex items-center gap-1 text-[11px] text-blue-700"><Copy className="h-3.5 w-3.5" /> Ya existe</span>;
  if (fila.ok)
    return <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" /> Válida</span>;
  return (
    <span className="inline-flex items-center gap-1 text-[11px] text-amber-700" title={fila.problemas.join(", ")}>
      <XCircle className="h-3.5 w-3.5" /> {fila.problemas[0]}
    </span>
  );
}

export default function BokunImporter() {
  const [cuentas, setCuentas] = useState([]);
  const [origen, setOrigen] = useState("api");
  const [cuentaId, setCuentaId] = useState("");
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [loading, setLoading] = useState(false);
  const [batch, setBatch] = useState(null);
  const [seleccion, setSeleccion] = useState(new Set());
  const [confirming, setConfirming] = useState(false);
  const [batches, setBatches] = useState([]);
  const fileRef = useRef(null);

  const loadCuentas = useCallback(async () => {
    try {
      const { data } = await api.get("/bokun/cuentas");
      setCuentas(data);
      if (data[0]) setCuentaId(data[0].id);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  }, []);

  const loadBatches = useCallback(async () => {
    try {
      const { data } = await api.get("/bokun/import/batches");
      setBatches(data);
    } catch (err) {
      /* noop */
    }
  }, []);

  useEffect(() => {
    loadCuentas();
    loadBatches();
  }, [loadCuentas, loadBatches]);

  const aplicarPreview = (data) => {
    setBatch(data);
    const sel = new Set((data.filas || []).filter((f) => f.ok && !f.duplicada).map((f) => f.row_id));
    setSeleccion(sel);
  };

  const previewApi = async () => {
    if (!cuentaId || !desde || !hasta) {
      toast.error("Elegí cuenta y rango de fechas.");
      return;
    }
    setLoading(true);
    setBatch(null);
    try {
      const { data } = await api.post("/bokun/import/preview-api", { cuenta_id: cuentaId, desde, hasta });
      aplicarPreview(data);
      toast.success(`Vista previa lista: ${data.total} reservas.`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const previewArchivo = async (file) => {
    if (!cuentaId) {
      toast.error("Elegí la cuenta de Bokun.");
      return;
    }
    if (!file) return;
    setLoading(true);
    setBatch(null);
    try {
      const fd = new FormData();
      fd.append("archivo", file);
      fd.append("cuenta_id", cuentaId);
      const { data } = await api.post("/bokun/import/preview-archivo", fd);
      aplicarPreview(data);
      toast.success(`Vista previa lista: ${data.total} filas.`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const toggleRow = (rowId) => {
    setSeleccion((s) => {
      const c = new Set(s);
      if (c.has(rowId)) c.delete(rowId);
      else c.add(rowId);
      return c;
    });
  };

  const confirmar = async () => {
    if (!batch || seleccion.size === 0) {
      toast.error("Seleccioná al menos una reserva para importar.");
      return;
    }
    setConfirming(true);
    try {
      const { data } = await api.post(`/bokun/import/batches/${batch.id}/confirm`, {
        incluir_ids: Array.from(seleccion),
      });
      toast.success(`Importadas ${data.insertadas} · actualizadas ${data.actualizadas} · omitidas ${data.omitidas}.`);
      setBatch(null);
      setSeleccion(new Set());
      loadBatches();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setConfirming(false);
    }
  };

  const descartar = async () => {
    if (!batch) return;
    try {
      await api.post(`/bokun/import/batches/${batch.id}/descartar`);
    } catch (e) {
      /* noop */
    }
    setBatch(null);
    setSeleccion(new Set());
  };

  const descargarPlantilla = async () => {
    try {
      await downloadFile("/bokun/import/plantilla-ejemplo", "plantilla_import_bokun.csv");
      toast.success("Plantilla de ejemplo descargada.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-4">
      <Card className="p-5 space-y-4">
        <div className="flex flex-col sm:flex-row gap-2 sm:items-center justify-between">
          <div>
            <p className="font-medium text-slate-900">Importar reservas históricas de Bokun</p>
            <p className="text-sm text-slate-500">
              Traé las reservas ya agendadas (julio, agosto, etc.) por API o desde un archivo exportado. Siempre con vista
              previa y validación antes de confirmar; no se duplican las que ya están en el sistema.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button variant={origen === "api" ? "default" : "outline"} size="sm" onClick={() => setOrigen("api")} data-testid="import-origen-api" className="gap-2">
            <Database className="h-4 w-4" /> Por API (rango de fechas)
          </Button>
          <Button variant={origen === "archivo" ? "default" : "outline"} size="sm" onClick={() => setOrigen("archivo")} data-testid="import-origen-archivo" className="gap-2">
            <FileSpreadsheet className="h-4 w-4" /> Por archivo (CSV / Excel)
          </Button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs">Cuenta de Bokun</Label>
            <Select value={cuentaId} onValueChange={setCuentaId}>
              <SelectTrigger data-testid="import-cuenta-select"><SelectValue placeholder="Elegí una cuenta" /></SelectTrigger>
              <SelectContent>
                {cuentas.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {origen === "api" ? (
            <>
              <div className="space-y-1.5">
                <Label className="text-xs">Desde</Label>
                <Input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} data-testid="import-desde-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Hasta</Label>
                <Input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} data-testid="import-hasta-input" />
              </div>
              <div className="flex items-end">
                <Button onClick={previewApi} disabled={loading} data-testid="import-preview-api-button" className="w-full gap-2">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />} Vista previa
                </Button>
              </div>
            </>
          ) : (
            <div className="space-y-1.5 sm:col-span-3">
              <Label className="text-xs">Archivo exportado de Bokun</Label>
              <div className="flex gap-2">
                <Input
                  ref={fileRef}
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={(e) => previewArchivo(e.target.files?.[0])}
                  disabled={loading}
                  data-testid="import-file-input"
                />
                {loading && <Loader2 className="h-5 w-5 animate-spin text-slate-400 self-center" />}
              </div>
              <p className="text-[11px] text-slate-400">
                <Upload className="h-3 w-3 inline" /> Las columnas se detectan automáticamente (Confirmation Code, Product,
                Date, Time, Adults, Children, Infants, etc.).
              </p>
              <Button
                type="button"
                variant="link"
                size="sm"
                onClick={descargarPlantilla}
                data-testid="import-plantilla-ejemplo"
                className="h-auto p-0 text-xs gap-1 text-slate-600"
              >
                <FileDown className="h-3.5 w-3.5" /> Descargar plantilla de ejemplo (CSV)
              </Button>
            </div>
          )}
        </div>
      </Card>

      {batch && (
        <Card className="p-0 overflow-hidden" data-testid="import-preview">
          <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-slate-100 bg-slate-50">
            <div className="flex items-center gap-3 text-sm">
              <ListChecks className="h-4 w-4 text-slate-500" />
              <span className="text-slate-700">
                {batch.total} filas · <span className="text-emerald-700">{batch.validas} válidas</span> ·{" "}
                <span className="text-amber-700">{batch.con_problemas} con problemas</span> ·{" "}
                <span className="text-blue-700">{batch.duplicadas} ya existen</span>
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500">{seleccion.size} seleccionadas</span>
              <Button variant="outline" size="sm" onClick={descartar} data-testid="import-descartar-button">Descartar</Button>
              <Button size="sm" onClick={confirmar} disabled={confirming || seleccion.size === 0} data-testid="import-confirm-button" className="gap-2">
                {confirming ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Confirmar import
              </Button>
            </div>
          </div>
          <div className="overflow-x-auto max-h-[460px] overflow-y-auto">
            <Table>
              <TableHeader className="sticky top-0 bg-white z-10">
                <TableRow className="hover:bg-white">
                  {["", "Código", "Pasajero", "Tour", "Fecha · Hora", "Pax", "Pago", "Estado"].map((h, i) => (
                    <TableHead key={i} className="text-xs uppercase tracking-wider text-slate-500">{h}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {batch.filas.map((f) => {
                  const n = f.normalizada;
                  return (
                    <TableRow key={f.row_id} data-testid={`import-row-${f.row_id}`} className={f.duplicada ? "bg-blue-50/30" : !f.ok ? "bg-amber-50/30" : ""}>
                      <TableCell>
                        <Checkbox
                          checked={seleccion.has(f.row_id)}
                          onCheckedChange={() => toggleRow(f.row_id)}
                          data-testid={`import-row-check-${f.row_id}`}
                        />
                      </TableCell>
                      <TableCell className="font-mono text-xs text-slate-600">{n.codigo_booking || "—"}</TableCell>
                      <TableCell className="text-sm text-slate-800">{n.nombre} {n.apellido}</TableCell>
                      <TableCell className="text-sm text-slate-600 max-w-[180px] truncate">
                        {f.tour?.nombre || n.bokun_product_title || "—"}
                      </TableCell>
                      <TableCell className="text-sm">
                        <span className="font-mono text-slate-700">{n.fecha || "—"}</span>
                        <span className="text-slate-400"> · {n.hora || "—"}</span>
                      </TableCell>
                      <TableCell className="text-sm text-slate-600 whitespace-nowrap">{paxResumen(n)}</TableCell>
                      <TableCell className="text-sm text-slate-600 whitespace-nowrap">
                        {n.monto_pagado > 0 || n.saldo_pendiente > 0
                          ? `$${n.monto_pagado} / $${n.saldo_pendiente}`
                          : "—"}
                      </TableCell>
                      <TableCell><FilaBadge fila={f} /></TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      {batches.length > 0 && (
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <History className="h-4 w-4 text-slate-500" />
            <p className="font-medium text-slate-800 text-sm">Importaciones recientes</p>
          </div>
          <div className="space-y-1.5">
            {batches.slice(0, 8).map((b) => (
              <div key={b.id} className="flex items-center justify-between text-xs text-slate-600 border-b border-slate-50 pb-1.5" data-testid={`import-batch-${b.id}`}>
                <span>
                  {b.origen === "api" ? "API" : "Archivo"} · {b.cuenta_nombre} ·{" "}
                  {new Date(b.created_at).toLocaleString("es")}
                </span>
                <span className="flex items-center gap-2">
                  <span className="text-slate-400">{b.total} filas</span>
                  {b.estado === "confirmado" && b.resultado ? (
                    <span className="text-emerald-700">✓ {b.resultado.insertadas} importadas</span>
                  ) : b.estado === "descartado" ? (
                    <span className="text-slate-400">descartado</span>
                  ) : (
                    <span className="text-amber-700">en vista previa</span>
                  )}
                </span>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
